import type { SendTransactionAppRequest } from "@/utils/tonConnect/hooks";

import { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";

import { hapticFeedback } from "@telegram-apps/sdk";
import { Address } from "@ton/core";
import { useSetAtom } from "jotai";

import { useTranslation } from "@arc/i18n";
import { trimAddress, useSendTransaction, useTonWallet } from "@arc/sdk";
import { NATIVE_TON_ADDRESS } from "@arc/shared/constants/tokens";
import { useAssetBalance } from "@arc/shared/hooks/useAssetBalance";
import { useFormatter } from "@arc/shared/hooks/useFormatter";
import { isValidAddress } from "@arc/shared/utils/isValidAddress";
import { isValidBoc } from "@arc/shared/utils/isValidBoc";
import { cn } from "@arc/ui/cn";
import { Drawer } from "@arc/ui/drawer";
import { List } from "@arc/ui/list";
import { ListItem } from "@arc/ui/list-item";

import { showMenuAtom } from "@/atoms/ui";
import { usePlatformFeature } from "@/hooks/platform";
import { sendBadRequestResponse } from "@/utils/tonConnect/createResponse";
import { sendEventToBridge } from "@/utils/tonConnect/requests";

import { usePincodeModal } from "./Pincode/usePincodeModal";

export const TonConnectTransactionModal = ({
  transaction,
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  transaction: SendTransactionAppRequest | null;
  onClose: (boc?: string, hasRequestErrors?: boolean) => void;
}) => {
  const { usePlatformData } = usePlatformFeature();
  const { platform } = usePlatformData();

  const { t } = useTranslation("wallet");
  const { formatFromNano } = useFormatter();

  const setShowMenu = useSetAtom(showMenuAtom);

  const [errors, setErrors] = useState<string[]>([]);

  const { promptPincode, PincodeModalComponent } = usePincodeModal();

  const { mutateAsync } = useSendTransaction();

  const activeWallet = useTonWallet();

  const balance = useAssetBalance({ assetAddress: NATIVE_TON_ADDRESS, walletAddress: activeWallet?.address });

  useEffect(() => {
    setShowMenu(!isOpen);
  }, [isOpen, setShowMenu]);

  useEffect(() => {
    if (!transaction) return;
    setErrors([]);

    const errorsAcc: string[] = [];

    if (!activeWallet) {
      errorsAcc.push(t("ERRORS.WALLET_NOT_CONNECTED"));
      return;
    }

    if (
      Number.isNaN(transaction.payload.valid_until) ||
      !["number", "undefined"].includes(typeof transaction.payload.valid_until)
    ) {
      errorsAcc.push(t("TONCONNECT.INVALID_VALID_UNTIL"));
    }

    if (typeof transaction.payload.valid_until === "number" && transaction.payload.valid_until < Date.now() / 1000) {
      errorsAcc.push(t("TONCONNECT.TRANSACTION_EXPIRED"));
    }

    if (!transaction.payload.messages.length) {
      errorsAcc.push(t("TONCONNECT.NO_MESSAGES"));
    }

    if (
      transaction.payload.messages.some(
        (message) =>
          !message.address ||
          !isValidAddress(message.address) ||
          Address.isRaw(message.address) ||
          !message.amount ||
          (
            typeof message.amount !== "string" &&
            typeof message.amount !== "number"
          ) ||
          (message.payload && !isValidBoc(message.payload)) ||
          (message.stateInit && !isValidBoc(message.stateInit)),
      )
    ) {
      errorsAcc.push(t("TONCONNECT.MESSAGES_HAS_INVALID_DATA"));
      console.log("errorsAcc", errorsAcc);
      console.log("transaction.payload.messages", transaction.payload.messages);
    }

    if (transaction.payload.network !== "-239") {
      errorsAcc.push(t("TONCONNECT.TESTNET_WARNING"));
    }

    // TODO: refactor duplicate code
    if (transaction.payload.from && !isValidAddress(transaction.payload.from)) {
      errorsAcc.push(t("TONCONNECT.FROM_ADDRESS_NOT_VALID"));
      setErrors(errorsAcc);

      sendEventToBridge({
        response: sendBadRequestResponse(+transaction.id, errorsAcc.join(", ")),
        sessionKeyPair: transaction.connection.sessionKeyPair,
        clientSessionId: transaction.connection.clientSessionId,
      });

      return;
    }

    if (
      transaction.payload.from &&
      !Address.parse(transaction.payload.from).equals(Address.parse(activeWallet.address))
    ) {
      errorsAcc.push(t("TONCONNECT.FROM_ADDRESS_NOT_EQUALS_WALLET_ADDRESS"));
    }

    if (errorsAcc.length) {
      setErrors(errorsAcc);

      sendEventToBridge({
        response: sendBadRequestResponse(+transaction.id, errorsAcc.join("\n")),
        sessionKeyPair: transaction.connection.sessionKeyPair,
        clientSessionId: transaction.connection.clientSessionId,
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeWallet, transaction]);

  const handleConfirmTx = useCallback(async () => {
    if (!transaction || !activeWallet) return;
    if (hapticFeedback.isSupported()) hapticFeedback.impactOccurred("medium");

    try {
      const mnemonics = await promptPincode("decode");

      if (!mnemonics) {
        toast.error(t("ERRORS.OPERATION_CANCELLED"));
        onClose();
        return;
      }

      const promise = mutateAsync({
        mnemonics,
        publicKey: activeWallet.publicKey,
        walletVersion: activeWallet.walletVersion,
        validUntil: transaction.payload.valid_until,
        messages: transaction.payload.messages.map((message) => ({
          // NOTE: we already check that amounts and addresses is not undefined in validation effect
          value: BigInt(message.amount!),
          to: Address.parse(message.address!),
          body: message.payload,
          stateInit: message.stateInit,
          bounce: Address.parseFriendly(message.address!).isBounceable,
        })),
      });

      await toast.promise(promise, {
        loading: t("TRANSACTION.LOADING"),
        success: t("TRANSACTION.SUCCESS"),
        error: (err) => err.message || t("TRANSACTION.ERROR"),
      });

      onClose(transaction.payload.messages[0]?.payload);
    } catch (err) {
      console.error(err);

      onClose();
    }
  }, [transaction, promptPincode, onClose, activeWallet, mutateAsync, t]);

  const amount = useMemo(
    () =>
      transaction?.payload.messages.reduce((acc, message) => acc + BigInt(message.amount || 0), BigInt(0)) || BigInt(0),
    [transaction?.payload.messages],
  );

  const isEnoughBalance = useMemo(() => {
    const maxInt = Number.MAX_SAFE_INTEGER;
    return BigInt(balance || maxInt) >= amount;
  }, [balance, amount]);

  const walletAddress = transaction?.payload.from || activeWallet?.address;

  const safeAreaTop =
    platform === "android" || platform === "ios" ? { paddingTop: "calc(env(safe-area-inset-top))" } : {};

  return (
    <>
      <Drawer height="full" isOpen={isOpen} onClose={() => onClose(undefined, !!errors.length)}>
        <div
          style={safeAreaTop}
          className="flex h-full flex-col items-center bg-[url('/images/connect-bg.svg')] bg-contain bg-[0px_-47px] bg-no-repeat p-4"
        >
          <div className="mt-16 flex w-full flex-col items-center justify-center">
            <div className="border-accent bg-tonconnect-app-logo-light dark:bg-tonconnect-app-logo-dark shadow-tonconnect-app-logo-light dark:shadow-tonconnect-app-logo-dark rounded-3xl border-4">
              <img
                alt={transaction?.connection.manifest?.name}
                className="h-24 w-24 rounded-3xl p-5"
                src={transaction?.connection.manifest.iconUrl}
              />
            </div>
            <div className="text-title-2 text-text mt-4 text-center font-medium">
              {transaction?.connection.manifest?.name}
            </div>
          </div>

          <div className="text-text mt-2 w-4/5 text-center text-base">{t("TONCONNECT.APP_ASK_CONFIRM")}</div>

          <div className="mt-12 w-full">
            <h3 className="text-caption-1 text-text-secondary mb-2 font-medium uppercase">{t("CHECKOUT.TITLE")}</h3>
            <List>
              <ListItem leftTopText={t("CHECKOUT.WALLET")} rightTopText={activeWallet?.walletName} />
              {walletAddress && isValidAddress(walletAddress) && (
                <ListItem
                  leftTopText={t("CHECKOUT.ADDRESS")}
                  rightTopText={trimAddress(Address.parse(walletAddress).toString())}
                />
              )}
              <ListItem leftTopText={t("CHECKOUT.SUMMARY")} rightTopText={`${formatFromNano(amount)} TON`} />
            </List>
          </div>

          {errors.length > 0 && (
            <ol className="text-negative text-headline mt-4 flex flex-col gap-1 whitespace-pre-wrap text-center">
              {errors.map((error) => (
                <li key={error}>{error}</li>
              ))}
            </ol>
          )}

          {!isEnoughBalance && (
            <div className="text-negative text-headline mt-4 text-center">{t("TONCONNECT.NOT_ENOUGH_BALANCE")}</div>
          )}

          <button
            className={cn("bg-accent mt-auto w-full rounded-2xl p-3 text-white disabled:opacity-50")}
            disabled={!transaction || errors.length > 0 || !isEnoughBalance}
            type="button"
            onClick={handleConfirmTx}
          >
            {t("UI.CONFIRM")}
          </button>
        </div>
      </Drawer>

      {isOpen && PincodeModalComponent}
    </>
  );
};
